// fetchLiftsByBank — the lifts of every bank of one organization.
//
// Resume hydration used to ask `GET /lifts/?filter_target=bank_id` once per
// bank, and each answer is authorized and preloaded server-side, so an org of
// thirty banks opened thirty requests at once. `GET /lifts/?org_id=` answers
// for the whole organization tree in one request (500 rows a page), and each
// row names its bank, so the banks' lifts are grouped here instead.
//
// A tree too large to page through cheaply (the server may widen `org_id` to
// everything the caller reaches) falls back to the per-bank requests.

import fetchFresh, { type Initiate } from "./fetchFresh";
import settleWithLimit, { HYDRATE_CONCURRENCY } from "./settleWithLimit";

/** Rows the server returns for one page: its own cap on `limit`. */
export const LIFT_PAGE_SIZE = 500;

/** Pages read before the whole-organization request gives up for the per-bank ones. */
export const MAX_LIFT_PAGES = 8;

/** What the `getLifts` endpoint is called with. */
export interface LiftsByOrgArg {
  page: number;
  limit: number;
  searchAndFilterString: string;
  token: string;
}

/** What the `getLiftsByBank` endpoint is called with. */
export interface LiftsByBankArg {
  bankId: string;
  page: number;
  limit: number;
  searchAndFilterString: string;
  token: string;
}

/** A lift row's bank, in either casing the decoders produce. */
function bankOf(row: any): unknown {
  return row?.bankId ?? row?.bank_id;
}

/** The rows of a paginated reply, however the base query wrapped them. */
function rowsOf(result: any): any[] {
  const rows = result?.data ?? result;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Reads the organization's lifts page by page. Resolves null when the tree
 * holds more than `MAX_LIFT_PAGES` pages, so the caller can ask per bank.
 */
async function fetchWholeOrg(
  dispatch: (action: never) => unknown,
  initiate: Initiate<LiftsByOrgArg>,
  orgId: string,
  token: string,
): Promise<any[] | null> {
  const rows: any[] = [];
  for (let page = 1; page <= MAX_LIFT_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- each page waits on the one before it
    const result: any = await fetchFresh(dispatch, initiate, {
      page,
      limit: LIFT_PAGE_SIZE,
      searchAndFilterString: `&org_id=${orgId}`,
      token,
    });
    const pageRows = rowsOf(result);
    rows.push(...pageRows);
    const total = Number(result?.total);
    const done = Number.isFinite(total) ? rows.length >= total : pageRows.length < LIFT_PAGE_SIZE;
    if (done || pageRows.length === 0) return rows;
  }
  return null;
}

/**
 * Resolves each bank id in `bankIds` to its lifts (a bank with none maps to an
 * empty list). A failed request rejects: an org shown with a bank's lifts
 * silently missing is worse than an error.
 *
 * `initiateOrg` is `liftAPI.endpoints.getLifts.initiate`, `initiateBank` is
 * `liftAPI.endpoints.getLiftsByBank.initiate`.
 */
export default async function fetchLiftsByBank(
  dispatch: (action: never) => unknown,
  initiateOrg: Initiate<LiftsByOrgArg>,
  initiateBank: Initiate<LiftsByBankArg>,
  orgId: string,
  bankIds: readonly string[],
  toId: (value: unknown) => string,
  token: string,
): Promise<Record<string, any[]>> {
  const byBank: Record<string, any[]> = Object.fromEntries(bankIds.map((id) => [id, []]));
  if (bankIds.length === 0) return byBank;

  const orgRows = await fetchWholeOrg(dispatch, initiateOrg, orgId, token);
  if (orgRows) {
    orgRows.forEach((row) => {
      const bank = bankOf(row);
      const id = bank ? toId(bank) : "";
      if (id in byBank) byBank[id].push(row);
    });
    return byBank;
  }

  const settled = await settleWithLimit(bankIds, HYDRATE_CONCURRENCY, (bankId) =>
    fetchFresh<LiftsByBankArg, any>(dispatch, initiateBank, {
      bankId,
      page: 1,
      limit: LIFT_PAGE_SIZE,
      searchAndFilterString: "",
      token,
    }),
  );
  settled.forEach((outcome, i) => {
    if (outcome.status === "rejected") throw outcome.reason;
    byBank[bankIds[i]] = rowsOf(outcome.value);
  });
  return byBank;
}
